import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} LicenseStats
 * @property {number} total_licenses - Total licenses purchased
 * @property {number} assigned_licenses - Licenses assigned
 * @property {number} available_licenses - Licenses available
 */

/**
 * @typedef {object} LicenseActivity
 * @property {string} date - Activity date
 * @property {string} action - Action performed (e.g., "Assigned")
 * @property {number} count - Number of licenses involved
 * @property {string|null} performed_by - User who performed the action
 * @property {string|null} remarks - Additional remarks
 */

/**
 * @typedef {object} LicenseOverviewData
 * @property {LicenseStats} stats - License statistics
 * @property {LicenseActivity[]} activity - License activity history
 */

/**
 * Custom hook to fetch license overview for an organization
 */
export const useLicenseOverview = () => {
  const [licenseData, setLicenseData] = useState(
    /** @type {LicenseOverviewData | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch License Overview
   * @async
   * @returns {Promise<void>}
   */
  const getLicenseOverview = async () => {
    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/organization/admin/licenses/overview`;
      const response = await axiosInstance.get(url);

      setLicenseData(response?.data?.data || null);
    } catch (err) {
      setLicenseData(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    licenseData,
    loading,
    error,
    getLicenseOverview,
  };
};

